<template>
  <d-card class="mb-4">
    <table class="table mb-0">
      <thead class="bg-primary-light">
        <tr>
          <th scope="col" class="border-0 d-flex">
            <span>{{ $t('product.productName') }}</span>

            <d-badge
              :theme="productData.exclude_in_invoice ? 'danger' : 'light'"
              class="pb-0 pt-0 pl-2 pr-0 ml-2 checkbox-wrapper erp-checkbox-wrapper">
              <d-checkbox
                inline
                v-model="productData.exclude_in_invoice"
                :disabled="readOnly"
                class="ml-0 mb-0">
                {{ $t('order.excludeInInvoice') }}
              </d-checkbox>
            </d-badge>

            <small
              class="ml-auto mt-0 cursor-help"
              :title="$t('product.quantityAvailableAtTimeOfOrdering', { quantity: productData.availability })">
              <template v-if="productData.quantity_collected || productData.quantity_collected === 0">{{ $t('product.collected') }}: </template>

              <template v-if="productData.quantity_collected === parseInt(productData.count, 10)">
                <strong class="text-success">{{ productData.quantity_collected }} {{ $t('ui.piecesAbbr') }}</strong>
              </template>
              <template v-else-if="productData.quantity_collected !== parseInt(productData.count, 10) && productData.quantity_collected >= 0">
                <strong class="text-warning">{{ productData.quantity_collected }} {{ $t('ui.piecesAbbr') }}</strong>
              </template>
              <template v-else-if="productData.quantity_collected">
                <strong class="text-danger">{{ productData.quantity_collected }} {{ $t('ui.piecesAbbr') }}</strong>
              </template>

              <template v-if="boxProductsCommentsCount > 0">
                <i
                  class="material-icons text-danger ml-2"
                  style="position: relative; top: 4px; cursor: pointer;"
                  @click="showCollectionComments(productData)">comment</i>
                <span class="mt-1 mr-2">
                  ({{ boxProductsCommentsCount }})
                </span>
              </template>
              <template v-else>
                <i
                  class="material-icons text-gray mr-2 mt-1"
                  style="position: relative; top: 4px; opacity: .25"
                  slot="prepend">comment</i>
              </template>
            </small>
          </th>
          <th scope="col" class="border-0" style="width: 150px;">
            <span>
              {{ $t('ui.amount') }}
            </span>

            <d-badge
              :theme="productData.moq ? 'success' : 'danger'"
              class="pb-0 pt-0 pl-1 pr-0 ml-1 checkbox-wrapper moq-checkbox-wrapper">
              <d-checkbox
                inline
                v-model="productData.moq"
                :disabled="readOnly"
                class="ml-0 mb-0">
                {{ $t('order.moq') }}
              </d-checkbox>
            </d-badge>
          </th>
          <th scope="col" class="border-0" style="width: 180px;">{{ $t('price.pricePerItem') }}</th>
          <th scope="col" class="border-0" style="width: 150px;">{{ $t('order.totalCost') }}</th>
          <th scope="col" class="border-0" style="width: 80px;" v-if="!readOnly">
            <template v-if="!isOrder || isInquiryMode">{{ $t('ui.actions') }}</template>
            <template v-else>{{ $t('ui.delete') }}</template>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td class="align-top d-flex is-v-select-wrapper flex-wrap">
            <div class="d-flex w-100">
              <validation-provider
                v-if="!readOnly"
                rules="required"
                :name="'product-id-' + productIndex"
                tag="div"
                class="w-100"
                v-slot="{ errors }">
                <div
                  v-if="!productData.id || productInDB"
                  :class="{ 'form-control w-100': true, 'is-select': true, 'is-invalid': errors.length }">
                  <v-select
                    :options="products"
                    v-model="productData.id"
                    @input="updateProductPrice($event); updateProductSaleState($event); addServicesIfNecessary($event);"
                    :reduce="product => product.id"
                    :filterBy="filterByForProducts"
                    :placeholder="$t('product.selectProduct')"
                    label="code">
                    <template slot="no-options">
                      {{ $t('product.noProductsMatchingGivenPhrase') }}&hellip;
                    </template>
                    <template slot="option" slot-scope="option">
                      <div class="d-center w-100 normal-white-space">
                        <d-badge
                          v-if="option.parent_id"
                          theme="success"
                          class="mr-2"
                          :key="'product-list-item-1-' + option.parent_id + '-' + option.id">
                          {{ $t('product.variantProduct') }}
                        </d-badge>
                        <d-badge
                          v-else
                          theme="secondary"
                          class="mr-2"
                          :key="'product-list-item-2-' + option.parent_id + '-' + option.id">
                          {{ $t('product.mainProduct') }}
                        </d-badge> {{ option.code }} - {{ option.name }}
                      </div>
                    </template>
                    <template slot="selected-option" slot-scope="option">
                      <div class="selected d-center w-100 normal-white-space">
                        <d-badge
                          v-if="option.parent_id"
                          theme="success"
                          class="mr-2"
                          :key="'product-list-item-3-' + option.parent_id + '-' + option.id">
                          {{ $t('product.variantProduct') }}
                        </d-badge>
                        <d-badge
                          v-else
                          theme="secondary"
                          class="mr-2"
                          :key="'product-list-item-4-' + option.parent_id + '-' + option.id">
                          {{ $t('product.mainProduct') }}
                        </d-badge> {{ option.code }} - {{ option.name }}
                      </div>
                    </template>
                  </v-select>
                </div>
                <div
                  v-else
                  class="form-control">
                  {{ productData.productCode }} - {{ productData.productName }}
                  <a
                    href="#"
                    @click.prevent="productData.id = null;">
                    <i class="material-icons">edit</i>
                    {{ $t('ui.edit') }}
                  </a>
                </div>
              </validation-provider>
              <template v-else>
                {{ productData.productCode }} - {{ productData.productName }}
              </template>

              <d-button
                v-if="productImportantInformation"
                theme="warning"
                class="ml-2"
                :title="$t('product.importantPriceInfoTitle')"
                @click.prevent="showImportantInformation(productImportantInformation)">
                <i class="material-icons">info</i>
              </d-button>

              <d-button
                v-if="!readOnly"
                class="ml-2 text-nowrap"
                outline
                @click.prevent="$bus.$emit('products-search-popup-open', productIndex)">
                <i class="material-icons">search</i>
                <template v-if="!isIndent">
                  {{ $t('ui.search') }}
                </template>
              </d-button>

              <d-button
                v-if="!readOnly && isIndent"
                class="ml-2 text-nowrap"
                outline
                @click.prevent="$bus.$emit('show-add-indent-product-popup', productIndex)">
                <i class="material-icons">add</i>
              </d-button>

              <d-btn
                v-if="productData.include_in_services && !productData.services.length && !!productData.id && !readOnly"
                @click.prevent="addService()"
                class="text-nowrap ml-2"
                outline
                theme="success">
                <i class="material-icons">add</i>
                {{ $t('service.addService') }}
              </d-btn>
            </div>

            <div
              v-if="selectedProductIsOnSale"
              class="pb-2">
              <small class="text-danger">
                * {{ $t('product.isSaleInfo') }}
              </small>
            </div>

            <div
              v-if="productData.embroidery_ended_production_at"
              class="mt-2 mb-1">
              {{ $t('product.embroideryEndedProductionAt') }}:
              <span class="text-primary">
                {{ productData.embroidery_ended_production_at }}
              </span>
            </div>

            <d-alert
              v-if="productData.purchase_product_id && !readOnly && isEditMode && parseInt(productData.count, 10) !== parseInt(productData.count_original, 10)"
              :show="true"
              theme="warning"
              class="mt-2 mb-1 px-2 py-1">
              <i class="material-icons">warning</i>
              {{ $t('order.purchaseQtyNeedsCheck') }}
            </d-alert>

            <small
              v-if="!userIsCustomer && productData.purchase_product_id && !readOnly"
              class="d-block w-100 mt-3">
              <div class="text-warning">
                <i class="material-icons">shopping_cart</i>
                {{ $t('purchase.reservation.reservedInOrder') }}: {{ productData.purchase_quantity }} {{ $t('ui.piecesAbbr') }}
              </div>

              <div class="mt-2 mb-2">
                <a
                  v-if="orderPurchase || productHasNewlyAddedPurchase(productData.purchase_product)"
                  href="#"
                  @click.prevent="editPurchaseReservation()"
                  class="text-primary mr-2"
                  key="edit-product-in-purchase">
                  <i class="material-icons">edit</i>
                  {{ $t('ui.edit') }}
                </a>

                <a
                  v-if="orderPurchase && orderPurchase.download_label_url"
                  :disabled="downloadingLabel !== false"
                  @click.prevent="downloadLabel(orderPurchase.download_label_url)"
                  class="text-primary mr-2"
                  :style="((downloadingLabel !== false) ? 'pointer-events: none;' : '')">
                  <template v-if="!downloadingLabel"> <i class="material-icons">cloud_download</i> {{ $t('order.warehouse.boxes.downloadLabel') }}</template>
                  <template v-else>
                    <div class="spinner-border spinner-border-sm" role="status"></div>
                  </template>
                </a>

                <a
                  v-if="orderPurchase || productHasNewlyAddedPurchase(productData.purchase_product)"
                  href="#"
                  @click.prevent="removePurchaseReservation('REMOVE_ASSIGN')"
                  class="text-danger mr-2"
                  key="remove-assignment-1">
                  <i class="material-icons">clear</i>
                  {{ $t('purchase.reservation.removeAssignment') }}
                </a>

                <a
                  v-else
                  href="#"
                  @click.prevent="cannotRemovePurchaseReservation()"
                  class="text-danger mr-2"
                  key="remove-assignment-2">
                  <i class="material-icons">clear</i>
                  {{ $t('purchase.reservation.removeAssignment') }}
                </a>

                <a
                  v-if="(orderPurchase && orderPurchase.can_delete_from_order) || productHasNewlyAddedPurchase(productData.purchase_product)"
                  href="#"
                  @click.prevent="removePurchaseReservation('REMOVE_FROM_PURCHASE')"
                  class="text-danger mr-2"
                  key="remove-from-purchase-1">
                  <i class="material-icons">clear</i>
                  {{ $t('purchase.reservation.removeFromPurchase') }}
                </a>

                <a
                  v-else
                  href="#"
                  @click.prevent="cannotRemovePurchaseReservation()"
                  class="text-danger mr-2"
                  key="remove-from-purchase-2">
                  <i class="material-icons">clear</i>
                  {{ $t('purchase.reservation.removeFromPurchase') }}
                </a>
              </div>

              <fieldset
                v-if="productData.purchase_product && (productData.purchase_product.size_name || productData.purchase_product.color_name || productData.purchase_product.polar_name || productData.purchase_product.material_name || productData.purchase_product.seam_name)"
                class="purchase-info-block mt-1 mb-2">
                <legend>
                  {{ $t('purchase.inOrder') }}
                  (<a :href="purchaseBaseUrl + 'zakupy/edycja/' + productData.purchase_product.purchase_id" target="_blank">#{{ productData.purchase_product.purchase_id }}</a>):
                </legend>
                <template v-if="productData.purchase_product.size_name">
                  <strong>{{ $t('purchase.size.size') }}:</strong> <span class="text-primary">{{ productData.purchase_product.size_name }}</span><br>
                </template>
                <template v-if="productData.purchase_product.color_name">
                  <strong>{{ $t('purchase.fabric.color') }}:</strong> <span class="text-primary">{{ productData.purchase_product.color_name }}</span><br>
                </template>
                <template v-if="productData.purchase_product.material_name">
                  <strong>{{ $t('purchase.fabric.fabric') }}:</strong> <span class="text-primary">{{ productData.purchase_product.material_name }}</span><br>
                </template>
                <template v-if="productData.purchase_product.polar_name">
                  <strong>{{ $t('purchase.fleece.fleece') }}:</strong> <span class="text-primary">{{ productData.purchase_product.polar_name }}</span><br>
                </template>
                <template v-if="productData.purchase_product.seam_name">
                  <strong>{{ $t('purchase.stitching.stitching') }}:</strong> <span class="text-primary">{{ productData.purchase_product.seam_name }}</span><br>
                </template>
              </fieldset>
            </small>

            <small
              v-if="!userIsCustomer && productData.containers && !isVirtual && productData.containers.length && !readOnly && !isOffer"
              class="d-block w-100">
              <div
                v-for="(container, index) of productData.containers"
                :key="'container-index-' + index"
                class="text-warning">
                <i class="material-icons">widgets</i>
                <template v-if="container.container">
                  {{ $t('container.reservation.reserevedInContainer') }} ({{ container.container.number }} - {{ container.container.date }}): {{ container.container_quantity }} {{ $t('ui.piecesAbbr') }}
                </template>
                <template v-else>
                  {{ $t('container.reservation.reserevedInContainer') }}: {{ container.container_quantity }} {{ $t('ui.piecesAbbr') }}
                </template>
                <a href="#" @click.prevent="removeContainerReservation(container.container_id)" class="text-danger">
                  <i class="material-icons">clear</i>
                  {{ $t('purchase.reservation.removeReservations') }}
                </a>
              </div>
            </small>

            <d-badge
              v-if="!userIsCustomer && productData.id && !readOnly && !productData.purchase_product_id && isAvailableInPurchase(productData.id) && selectedProductIsZZ"
              theme="danger"
              :outline="true"
              class="px-2 py-1 my-2 w-100 text-left">
              {{ $t('product.purchaseIsRequiredForZZProduct') }}
            </d-badge>

            <small
              v-if="!userIsCustomer && productData.id && !isVirtual && isAvailableInPurchase(productData.id) && !readOnly && !productData.purchase_product_id && !isOffer"
              class="d-block w-100 mt-2">
                <span
                  class="text-primary containers-trigger-link"
                  @click="showPurchasesPopup(productData.id, productData.product_id_in_order, productData.count - quantityToCompare, productData.containers)">
                  <i class="material-icons">shopping_cart</i>
                  {{ $t('product.checkAvailabilityInOrders') }}
                </span>

                <span
                  v-if="isEditMode"
                  class="text-primary containers-trigger-link pl-2"
                  @click="showPurchaseAddPopup(productData.id, productData.product_id_in_order, productData.count - quantityToCompare, productData.containers)">
                  <i class="material-icons">add_shopping_cart</i>
                  {{ $t('purchase.addOrder') }}
                </span>
            </small>

            <small
              v-if="!userIsCustomer && !readOnly && !isVirtual"
              class="d-block w-100 mt-2">
              <span
                class="text-primary containers-trigger-link"
                @click="showContainersPopup(productData.id, productData.product_id_in_order, productData.count - quantityToCompare, productData.containers, isOffer)">
                <i class="material-icons">widgets</i>
                {{ $t('product.checkAvailabilityInContainers') }}
              </span>
            </small>

            <d-alert
              v-if="newlyAddedReservations.length || userRemovedSomeReservations || userEditedSomeReservations"
              show
              theme="danger"
              class="mt-1">
              {{ $t('product.youMustSaveOrderToSaveReservations') }}
            </d-alert>

            <fieldset
              v-if="!userIsCustomer && globalServices.length"
              key="global-services-area"
              class="global-services w-100 px-3 pb-2 mt-2">
              <legend class="px-2">{{ $t('product.globalServices') }}</legend>

              <small
                v-for="(globalServicePackage, index) of globalServicesPackages"
                :key="'global-service-package-' + index"
                class="global-services-item d-flex mb-1">
                <template v-if="globalServicePackage.id">
                  <span class="w-75 align-self-center">
                    {{ getPackageNameByGroupId(globalServicePackage.group_id) }}
                  </span>
                  <d-input-group class="w-25">
                    <d-input
                      :min="0"
                      :step="1"
                      size="sm"
                      :disabled="readOnly"
                      v-model="globalPackagesQty[globalServicePackage.id]"
                      type="number"
                      class="text-right" />
                    <d-input-group-text slot="append">{{ $t('ui.piecesAbbr') }}</d-input-group-text>
                  </d-input-group>
                </template>
              </small>

              <small
                v-for="(globalService, index) of visibleGlobalServices"
                :key="'global-service-' + index"
                class="global-services-item d-flex mb-1">
                <template v-if="globalService.service_id_in_order">
                  <span class="w-75 align-self-center">
                    {{ globalService.name }}
                  </span>
                  <d-input-group class="w-25">
                    <d-input
                      :min="0"
                      :step="1"
                      :disabled="readOnly || !!globalService.service_package_id"
                      size="sm"
                      v-model="productData.global_services[globalService.service_id_in_order]"
                      type="number"
                      class="text-right" />
                    <d-input-group-text slot="append">{{ $t('ui.piecesAbbr') }}</d-input-group-text>
                  </d-input-group>
                </template>
                <template v-else-if="globalService.id">
                  <span class="w-75 align-self-center">
                    {{ globalService.serviceData.name }}
                  </span>
                  <d-input-group class="w-25">
                    <d-input
                      :min="0"
                      :step="1"
                      :disabled="readOnly || !!globalService.service_package_id"
                      size="sm"
                      v-model="productData.global_services[globalService.sid]"
                      type="number"
                      class="text-right" />
                    <d-input-group-text slot="append">{{ $t('ui.piecesAbbr') }}</d-input-group-text>
                  </d-input-group>
                </template>
              </small>
            </fieldset>
          </td>
          <td class="align-top">
            <validation-provider
              v-if="!readOnly"
              :rules="'required|min_value:' + (productData.moq ? (productsMinQty[productData.id] ? productsMinQty[productData.id] : 1) : 1).toString()"
              :name="'product-amount-' + productIndex"
              v-slot="{ errors }">
              <d-input-group>
                <d-input
                  :min="1"
                  :state="errors.length ? 'invalid' : null"
                  :disabled="productData.for_free"
                  v-model="productData.count"
                  type="number" />
                <d-input-group-text slot="append">{{ $t('ui.piecesAbbr') }}</d-input-group-text>
              </d-input-group>
            </validation-provider>
            <template v-else>
              {{ productData.count }} {{ $t('ui.piecesAbbr') }}
            </template>

            <small
              v-if="productData.moq && productsMinQty[productData.id] && productsMinQty[productData.id] > 1 && productsMinQty[productData.id] > productData.count"
              class="d-block text-danger">
              {{ $t('order.productMinQty') }}: {{ productsMinQty[productData.id] }} {{ $t('ui.piecesAbbr') }}
            </small>

            <template v-if="!isIndent && !userIsCustomer && canDisplayAvailabilityData">
              <small v-if="selectedProductAvailability !== false" class="d-block">
                <span :class="quantityToCompare < productData.count ? 'text-danger' : 'text-primary'">
                  {{ $t('ui.availability') }}: {{ quantityToCompare }} {{ $t('ui.piecesAbbr') }}
                  <template v-if="selectedProductAvailabilityNotSynced">
                    <br>
                    <span class="text-danger">
                      <i class="material-icons">warning</i>
                      {{ $t('product.availabilityUpdating') }}
                    </span>
                  </template>
                  <template v-if="productData.containers && productData.containers.length">
                    <br>
                    <span class="text-dark">{{ parseInt(productData.containers.reduce((sum, container) => sum + container.container_quantity, 0), 10) }} {{ $t('ui.piecesAbbr') }} {{ $t('product.inContainers') }}</span>
                  </template>
                  <template v-if="orderPurchase && orderPurchase.id && productData.purchase_product_id">
                    <br>
                    <span class="text-dark">{{ parseInt(productData.purchase_quantity, 10) }} {{ $t('ui.piecesAbbr') }} {{ $t('product.inOrder') }}</span>
                  </template>
                </span>
              </small>
              <small v-else class="d-block">
                {{ $t('ui.noAvailabilityData') }}
              </small>
            </template>
          </td>
          <td class="align-top">
            <validation-provider
              v-if="!readOnly"
              rules="required|min_value:0"
              :name="'product-' + productIndex"
              v-slot="{ errors }">
              <d-input-group :class="{'is-invalid': errors.length}">
                <input
                  :class="{
                    'form-control': true,
                    'is-invalid': errors.length
                  }"
                  :invalid="errors.length ? true : null"
                  :min="0"
                  :disabled="productData.for_free || !productData.use_custom_price"
                  type="number"
                  v-model="productData.price" />
                <d-input-group-text slot="append">
                  {{ selectedCurrencySymbol }}
                </d-input-group-text>
              </d-input-group>

              <d-form-invalid-feedback>
                {{ errors[0] }}
              </d-form-invalid-feedback>
            </validation-provider>
            <template v-else>
              {{ Number(productData.price).toFixed(2) }} {{ selectedCurrencySymbol }}
            </template>

            <d-checkbox
              inline
              v-model="productData.use_custom_price"
              :disabled="productData.for_free || readOnly"
              toggle
              toggle-small
              class="ml-0 mb-0 mt-2">
              <small style="display: block; line-height: 1.1;">
                {{ $t('order.useCustomPrice') }}
              </small>
            </d-checkbox>
          </td>
          <td class="align-top">
            <template v-if="productData.totalPrice && !Number.isNaN(productData.totalPrice)">
              <template v-if="customerProductsDiscount > 0 && productData.include_discounts && !productData.use_custom_price && !productData.for_free">
                <span class="text-primary" style="display: block; line-height: 1;">{{ productData.totalDiscountedPrice }} {{ selectedCurrencySymbol }}</span>
                <del style="display: block; line-height: 1;"><small>{{ productData.totalPrice }} {{ selectedCurrencySymbol }}</small></del>
              </template>
              <template v-else>
                <span class="text-primary">
                  {{ productData.totalPrice }} {{ selectedCurrencySymbol }}
                </span>
              </template>

              <small
                v-if="customerProductsDiscount > 0 && (!productData.include_discounts || productData.use_custom_price)"
                class="text-danger d-block">
                {{ $t('product.notIncludedInDiscounts') }}
              </small>
            </template>

            <d-checkbox
              inline
              v-model="productData.for_free"
              :disabled="readOnly"
              toggle
              toggle-small
              class="ml-0 mb-0 mt-2">
              <small style="display: block; line-height: 1.1;">
                {{ $t('order.forFree') }}
              </small>
            </d-checkbox>
          </td>
          <td
            v-if="!readOnly"
            class="align-top">
            <d-button
              outline
              :size="(!isOrder || isInquiryMode) ? 'sm' : null"
              :class="{ 'w-100': true, 'mb-1': (!isOrder || isInquiryMode) }"
              @click.prevent="removeProduct()"
              theme="danger">
              {{ $t('ui.delete') }}
            </d-button>
            <d-button
              v-if="!isOrder || isInquiryMode"
              outline
              size="sm"
              @click.prevent="duplicateProduct()"
              theme="primary">
              {{ $t('ui.duplicate') }}
            </d-button>
          </td>
        </tr>
        <tr v-if="productData.services.length">
          <td colspan="6">
            <h6 class="mb-0 pb-3 d-flex align-top">
              <div class="mt-1">{{ $t('service.services') }}</div>

              <d-button
                v-if="!readOnly"
                @click.prevent="addService()"
                theme="success"
                size="sm"
                outline
                class="ml-auto">
                <i class="material-icons">add</i>
                {{ $t('service.addService') }}
              </d-button>
            </h6>

            <table class="table table-sm table-bordered">
              <thead class="bg-success-light">
                <tr>
                  <th scope="col" class="border-0">
                    <small class="font-weight-bold">{{ $t('service.serviceAndContractor') }}</small>
                  </th>
                  <th scope="col" class="border-0" style="width: 100px;">
                    <small class="font-weight-bold">{{ $t('ui.piecesAmount') }}</small>
                  </th>
                  <th
                    :hidden="isIndent && isOffer"
                    scope="col" class="border-0" style="width: 180px;">
                    <small class="font-weight-bold">{{ $t('price.pricePerItemShort') }}</small>
                  </th>
                  <th
                    :hidden="isIndent && isOffer"
                    scope="col" class="border-0" style="width: 180px;">
                    <small class="font-weight-bold">{{ $t('service.setupPrice') }}</small>
                  </th>
                  <th
                    :hidden="isIndent && isOffer"
                    scope="col" class="border-0" style="width: 130px;">
                    <small class="font-weight-bold">{{ $t('order.totalCost') }}</small>
                  </th>
                  <th
                    v-if="!readOnly"
                    scope="col" class="border-0" style="width: 50px;">
                    <small outline class="font-weight-bold">{{ $t('ui.delete') }}</small>
                  </th>
                </tr>
              </thead>
              <tbody>
                <template v-for="(service, subindex) of productData.services">
                  <order-creator-service-package
                    v-if="firstServicesInPackage.indexOf(service.service_id_in_order) !== -1"
                    :key="'product-' + productIndex + '-service-package-' + subindex"
                    :ref="'product-' + productIndex + '-service-package-' + subindex"
                    :servicePackageID="service.service_package_id"
                    :servicePackages="productData.services_packages"
                    :productIndex="productIndex"
                    :isIndent="isIndent"
                    :isOffer="isOffer"
                    :readOnly="readOnly" />
                  <order-creator-service
                    :key="'product-' + productIndex + '-service-' + subindex"
                    :ref="'product-' + productIndex + '-service-' + subindex"
                    :contractors="contractors"
                    :currency-id="currencyId"
                    :read-only="readOnly"
                    :service-data="service"
                    :service-index="subindex"
                    :services="services"
                    :isIndent="isIndent"
                    :isOffer="isOffer"
                    :selected-currency-symbol="selectedCurrencySymbol"
                    :customer-products-discount="customerProductsDiscount"
                    :customer-services-discount="customerServicesDiscount"
                    :orderSetUpInProgress="orderSetUpInProgress"
                    :includeSetupPrice="includeSetupPrice"
                    :status="status"
                    :datesNotRequired="datesNotRequired"
                    :warnings="warnings"
                    :deadline="deadline"
                    :embroideryItems="embroideryItems"
                    :isFirstInPackage="firstServicesInPackage.indexOf(service.service_id_in_order) !== -1"
                    :isLastInPackage="lastServicesInPackage.indexOf(service.service_id_in_order) !== -1" />
                </template>
              </tbody>
            </table>
          </td>
        </tr>
        <tr v-if="productData.hasDescription">
          <td colspan="6">
            <label
              v-if="!readOnly"
              class="mb-0 mt-1">
              <small>{{ $t('product.productDescription') }}:</small>
            </label>
            <d-form-textarea
              v-if="!readOnly"
              :placeholder="$t('product.enterAdditionalProductDescriptionOptional')"
              :rows="3"
              :max-rows="6"
              :value.prop="productData.description"
              v-model="productData.description"></d-form-textarea>
            <template v-else>
              <br><strong>{{ $t('product.productDescription') }}:</strong><br>
              {{ productData.description }}
            </template>
          </td>
        </tr>
        <tr v-if="productData.hasDescriptionForCustomer">
          <td colspan="6">
            <label
              v-if="!readOnly"
              class="mb-0 mt-1">
              <small>{{ $t('product.productDescriptionForCustomer') }}:</small>
            </label>
            <d-form-textarea
              v-if="!readOnly"
              :placeholder="$t('product.enterAdditionalProductDescriptionForCustomerOptional')"
              :rows="3"
              :max-rows="6"
              :value.prop="productData.description_for_customer"
              v-model="productData.description_for_customer"></d-form-textarea>
            <template v-else>
              <br><strong>{{ $t('product.productDescriptionForCustomer') }}:</strong><br>
              <pre style="font-family: sans-serif;">{{ productData.description_for_customer }}</pre>
            </template>
          </td>
        </tr>
        <tr v-if="productData.hasVisualisation || productData.file">
          <td colspan="6">
            <a
              v-if="productData.file"
              href="#"
              @click.prevent="downloadVisualisation(productData.file_url)">
              <template v-if="!downloadInProgress">{{ $t('product.downloadVisualization') }}</template>
              <template v-if="downloadInProgress">{{ $t('ui.downloadInProgress') }}&hellip;</template>
            </a>

            <a
              v-if="!userIsCustomer && productData.file"
              href="#"
              @click.prevent="removeVisualisation"
              class="text-danger ml-3">
              <small>&times; {{ $t('ui.delete') }}</small>
            </a>

            <file-input
              v-if="!productData.file"
              :id="'inputFileVisual' + productData.id"
              :label="$t('product.selectFileWithVisualizationOptional')"
              :onChange="setVisualisation" />
          </td>
        </tr>
        <tr v-if="productData.hasDestroyedItems || productData.destroyed">
          <td colspan="6">
            <div class="d-flex">
              <div class="pt-1 pr-4 text-light">
                {{ $t('product.numberOfItemsDamagedInProduction') }}:
              </div>
              <d-input-group
                v-if="!readOnly"
                style="width: 150px;">
                <d-input
                  v-model="productData.destroyed"
                  type="number"
                  min="0"
                  step="1"
                  size="10"
                  class="text-right" />
                <d-input-group-text slot="append">
                  {{ $t('ui.piecesAbbr') }}
                </d-input-group-text>
              </d-input-group>
              <span
                v-else
                class="text-danger p-1">
                {{ productData.destroyed }} {{ $t('ui.piecesAbbr') }}
              </span>
            </div>
          </td>
        </tr>
        <tr v-if="!readOnly && (!productData.hasDescription || !productData.hasDescriptionForCustomer || !productData.hasVisualisation || (!productData.hasDestroyedItems && canAddDestroyedItemsInfo))">
          <td colspan="6">
            <a
              v-if="!productData.hasDescription"
              @click.prevent="productData.hasDescription = true;"
              href="#add-desc"
              class="mr-3">
              <small>
                <i class="material-icons">add</i> {{ $t('ui.addDescription') }}
              </small>
            </a>
            <a
              v-if="!productData.hasDescriptionForCustomer"
              @click.prevent="productData.hasDescriptionForCustomer = true;"
              href="#add-desc"
              class="mr-3">
              <small>
                <i class="material-icons">add</i> {{ $t('ui.addDescriptionForCustomer') }}
              </small>
            </a>
            <a
              v-if="!productData.hasVisualisation"
              href="#add-image"
              class="mr-3"
              @click.prevent="productData.hasVisualisation = true;">
              <small>
                <i class="material-icons">add</i> {{ $t('product.addVisualizationFile') }}
              </small>
            </a>
            <a
              v-if="!productData.hasDestroyedItems && canAddDestroyedItemsInfo"
              href="#add-destroyed"
              class="text-danger"
              @click.prevent="productData.hasDestroyedItems = true;">
              <small>
                <i class="material-icons">add</i> {{ $t('product.recordProductionDamage') }}
              </small>
            </a>
          </td>
        </tr>
      </tbody>
    </table>
  </d-card>
</template>

<script>
import AppConfig from './../../.env';
import CustomerPanel from './../../mixins/CustomerPanel.vue';
import FormUtils from './../../utils/FormUtils.js';
import OrderCreatorService from './OrderCreatorService';
import OrderCreatorServicePackage from './OrderCreatorServicePackage';
import OrderStatusPhase from './../../data/order-status-phase.js';
import Vue from 'vue';

export default {
  name: 'order-creator-product',
  mixins: [
    CustomerPanel
  ],
  props: [
    'contractors',
    'currencyId',
    'readOnly',
    'productData',
    'productIndex',
    'products',
    'productsMinQty',
    'services',
    'globalServices',
    'includeSetupPrice',
    'selectedCurrencySymbol',
    'customerProductsDiscount',
    'customerServicesDiscount',
    'orderSetUpInProgress',
    'globalServicesCount',
    'status',
    'warnings',
    'datesNotRequired',
    'collectAlert',
    'collectWarning',
    'isIndent',
    'isOffer',
    'isInquiryMode',
    'isOrder',
    'isVirtual',
    'canAddDestroyedItemsInfo',
    'deadline',
    'orderPurchases',
    'embroideryItems',
    'globalServicesPackages'
  ],
  components: {
    'order-creator-service': OrderCreatorService,
    'order-creator-service-package': OrderCreatorServicePackage
  },
  computed: {
    isEditMode () {
      return !!this.$route.params.id;
    },
    productInDB () {
      let foundedProduct = this.products.filter(product => product.id === this.productData.id);
      return foundedProduct && foundedProduct.length;
    },
    productImportantInformation () {
      let foundedProduct = this.products.filter(product => product.id === this.productData.id);


      if (foundedProduct && foundedProduct.length) {
        if (!foundedProduct[0].price_message) {
          return false;
        }

        return foundedProduct[0].price_message;
      }

      return false;
    },
    selectedProductAvailability () {
      let foundedProduct = this.products.filter(product => product.id === this.productData.id);

      if (foundedProduct && foundedProduct.length) {
        if (!foundedProduct[0].availability) {
          return 0;
        }

        return parseInt(foundedProduct[0].availability, 10);
      }

      return false;
    },
    selectedProductIsZZ () {
      let foundedProduct = this.products.filter(product => product.id === this.productData.id);

      if (foundedProduct && foundedProduct.length && foundedProduct[0].code && foundedProduct[0].code.indexOf('ZZ') === 0) {
        return true;
      }

      return false;
    },
    selectedProductIsOnSale () {
      if (this.$parent.productsData && this.$parent.productsData[this.productIndex]) {
        return !!this.$parent.productsData[this.productIndex].is_sale;
      }

      return false;
    },
    selectedProductAvailabilityNotSynced () {
      let foundedProduct = this.products.filter(product => product.id === this.productData.id);

      if (foundedProduct && foundedProduct.length) {
        let sum = foundedProduct[0].quantity_not_synchronized - foundedProduct[0].purchase_quantity_not_synchronized - foundedProduct[0].container_quantity_not_synchronized;
        return sum > 0;
      }

      return false;
    },
    quantityToCompare () {
      if (this.userIsCustomer) {
        return this.selectedProductAvailability;
      }

      return this.selectedProductAvailability + (this.productData.containers.reduce((sum, container) => container.container_quantity + sum, 0)) + parseInt(this.productData.purchase_quantity, 10);
    },
    canDisplayAvailabilityData () {
      return this.productData.id && (!this.status || OrderStatusPhase['display_availability'].indexOf(this.status) > -1);
    },
    purchaseBaseUrl () {
      return AppConfig.additionalConfig[AppConfig.useConfig].apiServerUrl;
    },
    boxProductsCommentsCount () {
      let commentsCount = 0;
      let product = this.productData;

      if (product.box_products) {
        let boxProductKeys = Object.keys(product.box_products);

        for (let i = 0; i < boxProductKeys.length; i++) {
          if (boxProductKeys[i] !== 'product_code') {
            let row = product.box_products[boxProductKeys[i]];

            if (row.collections && row.collections.length) {
              for (let j = 0; j < row.collections.length; j++) {
                if (row.collections[j].description) {
                  commentsCount++;
                }
              }
            }

            if (row.verifications && row.verifications.length) {
              for (let k = 0; k < row.verifications.length; k++) {
                if (row.verifications[k].description) {
                  commentsCount++;
                }
              }
            }

            if (row.returns && row.returns.length) {
              for (let k = 0; k < row.returns.length; k++) {
                if (row.returns[k].description) {
                  commentsCount++;
                }
              }
            }
          }
        }
      }

      return commentsCount;
    },
    orderPurchase () {
      if (this.productData.purchase_product && this.productData.purchase_product.purchase_id) {
        let purchase = this.orderPurchases.filter(purchaseData => purchaseData.id === this.productData.purchase_product.purchase_id);

        if (purchase[0]) {
          return purchase[0];
        }

        return null;
      }

      return null;
    },
    firstServicesInPackage () {
      let foundedPackageIds = [];
      let ids = [];

      for (let i = 0; i < this.productData.services.length; i++) {
        let service = this.productData.services[i];

        if (service.service_package_id && foundedPackageIds.indexOf(service.service_package_id) === -1) {
          foundedPackageIds.push(service.service_package_id);
          ids.push(service.service_id_in_order);
        }
      }

      return ids;
    },
    lastServicesInPackage () {
      let ids = {};
      let output = [];

      for (let i = 0; i < this.productData.services.length; i++) {
        let service = this.productData.services[i];

        if (service.service_package_id) {
          ids[service.service_package_id] = service.service_id_in_order;
        }
      }

      let packageIDs = Object.keys(ids);

      for (let i = 0; i < packageIDs.length; i++) {
        output.push(ids[packageIDs[i]]);
      }

      return output;
    },
    visibleGlobalServices () {
      return this.globalServices.filter(service => !service.service_package_id);
    }
  },
  watch: {
    'productData.for_free': function (newValue) {
      if (newValue) {
        this.$parent.productsData[this.productIndex].price = '0.01';
        this.$parent.productsData[this.productIndex].count = 1;
        this.$parent.productsData[this.productIndex].use_custom_price = false;
      }
    },
    'globalServicesPackages': {
      handler () {
        this.syncGlobalPackagesQty();
      }
    },
    'globalPackagesQty': {
      handler (newValue) {
        let IDs = Object.keys(newValue);

        for (let i = 0; i < IDs.length; i++) {
          let valueToSet = newValue[IDs[i]];

          for (let j = 0; j < this.globalServices.length; j++) {
            if ((this.globalServices[j].service_package_id).toString() === (IDs[i]).toString()) {
              Vue.set(this.productData.global_services, this.globalServices[j].service_id_in_order, valueToSet);
            }
          }
        }
      },
      deep: true
    }
  },
  data () {
    return {
      downloadInProgress: false,
      newlyAddedReservations: [],
      userRemovedSomeReservations: false,
      userEditedSomeReservations: false,
      downloadingLabel: false,
      visiblePackages: [],
      globalPackagesQty: {}
    };
  },
  mounted () {
    this.$bus.$on('order-change-currency', this.updateCurrency);
    this.$bus.$on('order-change-product-from-popup', this.updateProductDataFromPopup);
    this.$bus.$on('order-product-container-reservation', this.setContainerReservation);
    this.$bus.$on('order-product-purchase-reservation', this.setPurchaseReservation);
    this.$bus.$on('purchase-edit-operation-results', this.updatePurchaseReservation);
    this.syncGlobalPackagesQty();
  },
  methods: {
    syncGlobalPackagesQty () {
      for (let i = 0; i < this.globalServicesPackages.length; i++) {
        let pkg = this.globalServicesPackages[i];

        if (pkg.id && !Object.prototype.hasOwnProperty.call(this.globalPackagesQty, pkg.id)) {
          let initialValue = 0;

          for (let j = 0; j < this.globalServices.length; j++) {
            if (this.globalServices[j].service_package_id === pkg.id) {
              let serviceId = this.globalServices[j].service_id_in_order;

              if (this.productData.global_services[serviceId] !== undefined) {
                initialValue = this.productData.global_services[serviceId];
                break;
              }
            }
          }

          Vue.set(this.globalPackagesQty, pkg.id, initialValue);
        }
      }
    },
    filterByForProducts (option, label, search) {
      return (option.code + option.name).toLowerCase().indexOf(search.toLowerCase()) > -1;
    },
    addService () {
      this.$parent.productsData[this.productIndex].services.push({
        sid: +new Date(),
        id: '',
        serviceData: {},
        price: '',
        count: this.$parent.productsData[this.productIndex].count,
        setupPrice: '',
        totalPrice: '',
        exclude_in_invoice: false,
        use_custom_price: false,
        samples: [],
        embroidery_estimated_date: null,
        embroidery_date_tokens: [],
        service_package_id: null
      });
    },
    removeProduct () {
      if (this.productData.purchase_product_id) {
        Vue.swal({
          title: this.$t('product.cannotRemoveDuePurchaseTitle'),
          text: this.$t('product.cannotRemoveDuePurchaseDesc'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        return;
      }

      this.$parent.productsData.splice(this.productIndex, 1);
    },
    duplicateProduct () {
      let clone = JSON.parse(JSON.stringify(this.$parent.productsData[this.productIndex]));
      delete clone.product_id_in_order;

      if (clone.services && clone.services.length) {
        for (let i = 0; i < clone.services.length; i++) {
          if (clone.services[i].service_id_in_order) {
            delete clone.services[i].service_id_in_order;
          }

          Vue.set(clone.services[i], 'samples', []);
          Vue.set(clone.services[i], 'files', []);
          Vue.set(clone.services[i], 'is_cloned', true);
        }
      }

      this.$parent.productsData.push(clone);
    },
    updateProductSaleState (productID) {
      let foundedProduct = this.products.filter(product => product.id === productID);
      let isSale = 0;

      if (foundedProduct && foundedProduct.length && foundedProduct[0].is_sale) {
        isSale = 1;
      }

      this.$parent.productsData[this.productIndex].is_sale = isSale;
    },
    updateProductPrice (productID) {
      // For free products - skip price udpate
      if (this.productData.for_free) {
        return;
      }

      if (this.productData.use_custom_price) {
        this.productData.use_custom_price = false;
      }

      let productItem = this.products.filter(product => product.id === productID)[0];

      if (!productItem) {
        this.$parent.productsData[this.productIndex].price = '0.00';
        return;
      }

      this.$parent.productsData[this.productIndex].productData = JSON.parse(JSON.stringify(productItem));
      this.$parent.productsData[this.productIndex].include_in_services = productItem.include_in_services;
      this.$parent.productsData[this.productIndex].include_discounts = productItem.include_discounts;
      let productPrice = productItem.prices.filter(price => price.currency_id === this.currencyId)[0];

      if (!this.$parent.productsData[this.productIndex].include_in_services) {
        this.$parent.productsData[this.productIndex].services = [];
      }

      if (!productPrice) {
        this.$parent.productsData[this.productIndex].price = '0.00';
        return;
      }

      this.$parent.productsData[this.productIndex].price = Number(parseFloat(productPrice.price)).toFixed(2);
    },
    addServicesIfNecessary (productID) {
      let productItem = this.products.filter(product => product.id === productID)[0];

      Vue.nextTick(() => {
        if (productItem && productItem.default_services && productItem.default_services.length) {
          if (this.$parent.productsData[this.productIndex].services.length > 0) {
            Vue.swal({
              title: this.$t('product.defaultServicesConfirm.cannotAddDefaultServicesTitle'),
              text: this.$t('product.defaultServicesConfirm.cannotAddDefaultServicesText'),
              type: 'warning',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });

            return;
          }

          Vue.swal({
            title: this.$t('product.defaultServicesConfirm.doYouWantToAddDefaultServicesTitle'),
            text: this.$t('product.defaultServicesConfirm.doYouWantToAddDefaultServicesTitle'),
            type: 'question',
            showCancelButton: true,
            confirmButtonText: this.$t('product.defaultServicesConfirm.yesPleaseAdd'),
            cancelButtonText: this.$t('ui.cancel')
          }).then((result) => {
            if (result.value) {
              for (let i = 0; i < productItem.default_services.length; i++) {
                this.addService();
              }

              for (let i = 0; i < this.$parent.productsData[this.productIndex].services.length; i++) {
                let serviceID = productItem.default_services[i];
                this.$parent.productsData[this.productIndex].services[i].id = serviceID;
                Vue.nextTick(() => {
                  console.log('Update default service', this.productIndex, i, serviceID, this.$refs['product-' + this.productIndex + '-service-' + i]);
                  this.$refs['product-' + this.productIndex + '-service-' + i][0].updateServicePrices(serviceID);
                });
              }
            }
          });
        }
      });
    },
    updateProductPriceWithoutOverride () {
      // For free products - skip price update
      if (this.productData.for_free) {
        return;
      }

      if (this.productData.use_custom_price) {
        this.productData.use_custom_price = false;
      }

      let productItem = this.$parent.productsData[this.productIndex];

      if (!productItem) {
        this.$parent.productsData[this.productIndex].price = '0.00';
        return;
      }

      let productPrice = productItem.productData.prices.filter(price => price.currency_id === this.currencyId)[0];

      if (!productPrice) {
        this.$parent.productsData[this.productIndex].price = '0.00';
        return;
      }

      this.$parent.productsData[this.productIndex].price = Number(parseFloat(productPrice.price)).toFixed(2);
    },
    updateServices () {
      this.$bus.$emit('order-update-service-prices', this.productIndex);
    },
    updateCurrency () {
      if (this.orderSetUpInProgress) {
        return;
      }

      Vue.nextTick(() => {
        this.updateProductPriceWithoutOverride();
        this.updateServices();
      });
    },
    setVisualisation (e) {
      this.productData.fileToUpload = e.target.files[0];
    },
    removeVisualisation () {
      this.productData.file = '';
      this.productData.file_url = null;
      this.productData.hasVisualisation = false;
    },
    downloadVisualisation (url) {
      if (this.downloadInProgress) {
        return;
      }

      this.downloadInProgress = true;

      this.$http.post(url, {}, {
        responseType: 'blob'
      }).then((response) => {
        let extension = response.headers['content-type'].split('/')[1].replace(/[^a-z0-9]/gmi, '');
        let link = document.createElement('a');
        link.href = URL.createObjectURL(new Blob([response.data]));
        link.setAttribute('download', this.$t('product.productVisualizationFileNamePrefix') + this.productData.id + '.' + extension);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadInProgress = false;
      }).catch(() => {
        this.downloadInProgress = false;
      });
    },
    showContainersPopup (productID, orderProductID, missingAmount, containers, isOffer) {
      this.$bus.$emit('container-search-popup-open', parseInt(productID, 10), parseInt(orderProductID, 10), parseInt(missingAmount, 10), containers, isOffer);
    },
    setContainerReservation (productID, orderProductID, containerID, containerQuantity) {
      if (!(this.productData.id === productID && (this.productData.product_id_in_order === orderProductID || (!this.productData.product_id_in_order && orderProductID === 0)))) {
        return;
      }

      this.productData.containers.push({
        container_id: containerID,
        container_quantity: containerQuantity
      });

      this.newlyAddedReservations.push('container-' + containerID);
    },
    removeContainerReservation (containerID) {
      let filteredContainers = this.productData.containers.filter(container => container.container_id !== containerID);
      Vue.set(this.productData, 'containers', filteredContainers);
      this.newlyAddedReservations = this.newlyAddedReservations.filter(item => item !== 'container-' + containerID);
      this.userRemovedSomeReservations = true;
    },
    showPurchasesPopup (productID, orderProductID, missingAmount) {
      this.$bus.$emit(
        'purchase-search-popup-open',
        parseInt(productID, 10),
        parseInt(orderProductID || 0, 10),
        parseInt(missingAmount, 10),
        this.orderPurchase ? this.orderPurchase.id : 0
      );
    },
    setPurchaseReservation (productID, orderProductID, purchaseProductID, purchaseID, purchaseQuantity, purchaseData) {
      if (!(this.productData.id === productID && (this.productData.product_id_in_order === orderProductID || (!this.productData.product_id_in_order && orderProductID === 0)))) {
        return;
      }

      this.productData.purchase_product_id = purchaseProductID;
      this.productData.purchase_quantity = purchaseQuantity;
      this.productData.purchase_product = purchaseData;

      // We need to increase reserved quantity during making purchase reservation for proper calculations while editing purchase
      this.productData.purchase_product.quantity_reserved = this.productData.purchase_product.quantity_reserved + parseInt(purchaseQuantity, 10);

      Vue.nextTick(() => {
        this.newlyAddedReservations.push('purchase-' + purchaseID);
      });
    },
    editPurchaseReservation () {
      this.$bus.$emit('purchase-edit-popup-open', this.productData.id, this.productData.purchase_product_id, this.productData.product_id_in_order, {
        purchaseID: this.productData.purchase_product.purchase_id,
        purchaseProductQuantity: this.productData.purchase_product.quantity,
        purchaseProductReservedQuantity: this.productData.purchase_product.quantity_reserved,
        orderProductReservedQuantity: this.productData.purchase_quantity
      }, this.orderPurchase);
    },
    removePurchaseReservation (operationType = 'REMOVE_ASSIGN') {
      let popupTitle = this.$t('product.reservationRemoveAssignmentQuestion');
      let popupText = this.$t('product.reservationRemoveAssignmentQuestionDesc');
      let popupErrorText = this.$t('product.reservationRemoveAssignmentFailDesc');
      let removeSuccessTitle = this.$t('product.reservationAssignmentRemoveSuccess');
      let removeSuccessText = this.$t('product.reservationAssignmentRemoveSuccessDesc');
      let removeErrorTitle = this.$t('product.reservationAssignmentRemoveFail');
      let removeErrorText = this.$t('product.reservationAssignmentRemoveFailDesc');

      if (operationType === 'REMOVE_FROM_PURCHASE') {
        popupTitle = this.$t('product.reservationRemoveFromPurchaseQuestion');
        popupText = this.$t('product.reservationRemoveFromPurchaseQuestionDesc');
        popupErrorText = this.$t('product.reservationRemoveFromPurchaseErrorDesc');
        removeSuccessTitle = this.$t('product.reservationRemoveFromPurchaseSuccess');
        removeSuccessText = this.$t('product.reservationRemoveFromPurchaseSuccessDesc');
        removeErrorTitle = this.$t('product.reservationRemoveFromPurchaseFail');
        removeErrorText = this.$t('product.reservationRemoveFromPurchaseFailDesc');
      }

      let formDataToSend = {
        type: operationType
      };

      if (operationType === 'REMOVE_ASSIGN') {
        formDataToSend.order_product_id = this.productData.product_id_in_order;
      }

      Vue.swal({
        title: popupTitle,
        text: popupText,
        type: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('ui.yesRemove'),
        cancelButtonText: this.$t('ui.cancel')
      }).then((result) => {
        if (result.value) {
          FormUtils.submitInBackground({
            $http: this.$http,
            endpoint: '/purchases/remove-product-from-purchase/' + this.productData.purchase_product_id,
            formData: formDataToSend,
            errorTitle: this.$t('ui.errorOccured'),
            errorText: popupErrorText,
            successAction: (response) => {
              if (response.success) {
                if (response.removed && response.removed.purchase_product_id) {
                  Vue.set(this.productData, 'purchase_product_id', 0);
                  Vue.set(this.productData, 'purchase_quantity', 0);
                  Vue.set(this.productData, 'purchase_product', null);
                  this.userRemovedSomeReservations = true;
                }

                if (response.removed && response.removed.purchase_id) {
                  Vue.nextTick(() => {
                    this.newlyAddedReservations = this.newlyAddedReservations.filter(item => item.indexOf('purchase-') > -1);
                    this.userRemovedSomeReservations = true;
                  });
                }

                Vue.swal({
                  title: removeSuccessTitle,
                  text: removeSuccessText,
                  type: 'success',
                  confirmButtonText: this.$t('ui.ok'),
                  buttonsStyling: true
                });
              } else {
                Vue.swal({
                  title: this.$t('purchase.reservation.cannotBeRemoved'),
                  text: this.$t('purchase.reservation.cannotBeRemovedDesc', { customerName: this.orderPurchase ? this.orderPurchase.customer_name : '' }),
                  type: 'warning',
                  confirmButtonText: this.$t('ui.ok'),
                  buttonsStyling: true
                });
              }
            },
            failAction: () => {
              Vue.swal({
                title: removeErrorTitle,
                text: removeErrorText,
                type: 'warning',
                confirmButtonText: this.$t('ui.ok'),
                buttonsStyling: true
              });
            }
          });
        }
      });
    },
    cannotRemovePurchaseReservation () {
      Vue.swal({
        title: this.$t('purchase.reservation.cannotBeRemoved'),
        text: this.$t('purchase.reservation.cannotBeRemovedDesc', { customerName: this.orderPurchase ? this.orderPurchase.customer_name : '' }),
        type: 'warning',
        confirmButtonText: this.$t('ui.ok'),
        buttonsStyling: true
      });
    },
    showPurchaseAddPopup (productID, orderProductID, missingAmount) {
      this.$bus.$emit(
        'purchase-add-popup-open',
        parseInt(productID, 10),
        parseInt(orderProductID || 0, 10),
        parseInt(missingAmount, 10),
        this.orderPurchase ? this.orderPurchase.id : 0
      );
    },
    isAvailableInPurchase (productID) {
      let foundedProduct = this.products.filter(product => product.id === productID);

      if (foundedProduct.length) {
        return !!foundedProduct[0].available_in_purchase;
      }

      return false;
    },
    updateProductDataFromPopup (productIndex, productID) {
      if (productIndex === this.productIndex) {
        this.updateProductPrice(productID);
        this.updateProductSaleState(productID);
        this.addServicesIfNecessary(productID);
      }
    },
    productHasNewlyAddedPurchase (purchaseProduct) {
      if (purchaseProduct && purchaseProduct.purchase_id && this.newlyAddedReservations.indexOf('purchase-' + purchaseProduct.purchase_id) > -1) {
        return true;
      }

      return false;
    },
    updatePurchaseReservation (newConfig) {
      if (
        !(
          this.productData.id === newConfig.productID &&
          (
            this.productData.product_id_in_order === newConfig.orderProductID ||
            (!this.productData.product_id_in_order && newConfig.orderProductID === 0)
          )
        )
      ) {
        return;
      }

      Vue.set(this.productData, 'purchase_quantity', newConfig.quantity);
      Vue.set(this.productData.purchase_product, 'quantity', newConfig.purchaseQuantity);
      Vue.set(this.productData.purchase_product, 'quantity_reserved', newConfig.newQuantityReserved);
      this.userEditedSomeReservations = true;
    },
    showCollectionComments (productData) {
      this.$bus.$emit('order-creator-box-products-comments-popup-open', productData.box_products);
    },
    downloadLabel (labelURL) {
      this.downloadingLabel = true;

      this.$http.post(labelURL, null, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        let filenamePrefix = this.$t('shipments.labelFilePrefix');
        link.setAttribute('download', filenamePrefix + this.orderPurchase.id + '.pdf');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadingLabel = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadingLabel = false;
      });
    },
    showImportantInformation (infoToShow) {
      Vue.swal({
        title: '',
        html: infoToShow,
        type: 'info',
        confirmButtonText: this.$t('ui.ok'),
        buttonsStyling: true
      });
    },
    getPackageNameByGroupId (groupID) {
      let foundedPackage = this.globalServicesPackages.filter(pkg => pkg.group_id === groupID);

      if (foundedPackage.length) {
        return foundedPackage[0].group_name;
      }

      return '';
    }
  },
  beforeDestroy () {
    this.$bus.$off('order-change-currency', this.updateCurrency);
    this.$bus.$off('order-change-product-from-popup', this.updateProductDataFromPopup);
    this.$bus.$off('order-product-container-reservation', this.setContainerReservation);
    this.$bus.$off('order-product-purchase-reservation', this.setPurchaseReservation);
    this.$bus.$off('purchase-edit-operation-results', this.updatePurchaseReservation);
  }
}
</script>

<style lang="scss" scoped>
.is-v-select-wrapper > div {
  width: 100%;
}

.cursor-help {
  cursor: help;
}

.th-toggle {
  height: 10px;

  /deep/ small {
    position: relative;
    top: -2px;
  }
}

.containers-trigger-link {
  cursor: pointer;
}

.purchase-info-block {
  border: 1px solid #ccc;
  border-radius: 3px;
  padding: 0 10px 5px 10px;

  legend {
    display: inline-block;
    font-size: 12px;
    font-weight: bold;
    margin: 0 -5px;
    padding: 0 5px;
    width: auto;
  }
}

.global-services {
  border: 1px solid #ddd;

  legend {
    font-size: 14px;
  }
}

.normal-white-space {
  line-height: 1.2;
  white-space: normal;
}

.erp-checkbox-wrapper.badge-light {
  background: transparent!important;
}
</style>
